import { PropsWithChildren, useEffect, useMemo } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import {
  LiquidityListReq,
  LiquidityResponse,
  RawLiquidityResponse,
} from '#/leaderboard/entities/liquidity';
import { processLiquidityResponse } from '#/leaderboard/repositories/adapters';
import { UserFirm } from '#/leaderboard/entities/shared';
import { listLiquidityResponses } from '#/leaderboard/repositories/liquidity';
import { useStoredLeaderboardFetcher } from '#/leaderboard/contexts/fetcher';
import {
  useLiquidityFilterActions,
  useLiquidityFilterState,
} from '#/leaderboard/contexts/liquidity-filter';

interface LiquidityResponsesState {
  readonly liquidityResponses: LiquidityResponse[];
  readonly userFirm: UserFirm | null;
  readonly status: 'loading' | 'idle' | 'error';
  readonly error: string | null;
  readonly lastUpdatedAt: number | null;
}
export const [useLiquidityResponsesState, LiquidityResponsesStateProvider] =
  createSafeContext<LiquidityResponsesState>('LiquidityResponsesState');

export default function LiquidityResponsesListProvider({
  children,
}: PropsWithChildren) {
  const filterActions = useLiquidityFilterActions();

  const {
    status,
    error,
    list,
    userFirm,
    lastUpdatedAt,
    fetchLeaderboard: fetchResponsesList,
  } = useStoredLeaderboardFetcher<RawLiquidityResponse, LiquidityListReq>({
    filterActions,
    fetchFn: listLiquidityResponses,
    key: 'liquidity-responses',
  });

  const filters = useLiquidityFilterState();

  const state = useMemo(
    () => ({
      liquidityResponses: list.map((v) =>
        processLiquidityResponse(v, userFirm),
      ),
      status,
      error,
      userFirm,
      lastUpdatedAt,
    }),
    [list, status, error, userFirm, lastUpdatedAt],
  );

  useEffect(() => {
    fetchResponsesList(filters);
  }, [fetchResponsesList, filters]);

  return (
    <LiquidityResponsesStateProvider value={state}>
      {children}
    </LiquidityResponsesStateProvider>
  );
}
